import type { AttemptStatus, ContractSpec, OrderStatus } from '@head-of-trading/contracts';
import type { Language } from '../i18n/context.js';

const localeOf = (language: Language): 'en-US' | 'fr-FR' => language === 'fr' ? 'fr-FR' : 'en-US';

export const formatMoney = (value: number, compact: boolean, language: Language): string => {
  const locale = localeOf(language);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    notation: compact && Math.abs(value) >= 1_000 ? 'compact' : 'standard',
    minimumFractionDigits: compact && Math.abs(value) >= 1_000 ? 0 : 2,
    maximumFractionDigits: compact && Math.abs(value) >= 1_000 ? 2 : 2
  }).format(value);
};

export const formatUsd = (value: number, digits: number, language: Language): string => new Intl.NumberFormat(localeOf(language), {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'narrowSymbol',
  minimumFractionDigits: digits,
  maximumFractionDigits: digits
}).format(value);

export const formatInteger = (value: number, language: Language): string => new Intl.NumberFormat(localeOf(language), { maximumFractionDigits: 0 }).format(value);
export const formatNumber = (value: number, language: Language): string => new Intl.NumberFormat(localeOf(language), { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(value);
export const formatPct = (value: number, digits: number, language: Language): string => new Intl.NumberFormat(localeOf(language), {
  style: 'percent',
  minimumFractionDigits: digits,
  maximumFractionDigits: digits
}).format(value);

export const formatClock = (milliseconds: number): string => {
  const total = Math.max(0, Math.floor(milliseconds / 1_000));
  const h = Math.floor(total / 3_600) % 24;
  const m = Math.floor(total / 60) % 60;
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

export const formatDuration = (milliseconds: number): string => {
  const total = Math.max(0, Math.ceil(milliseconds / 1_000));
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

export const parseLocaleNumber = (input: string, language: Language = 'en'): number => {
  const compact = input.trim().replace(/\s/g, '');
  const comma = compact.lastIndexOf(',');
  const dot = compact.lastIndexOf('.');
  const normalized = comma >= 0 && dot >= 0
    ? comma > dot
      ? compact.replace(/\./g, '').replace(',', '.')
      : compact.replace(/,/g, '')
    : language === 'fr' && comma >= 0
      ? compact.replace(',', '.')
      : language === 'en'
        ? compact.replace(/,/g, '')
        : compact;
  return Number(normalized);
};

export function formatPrice(value: number, spec: ContractSpec | undefined, language: Language): string {
  if (spec?.quoteStyle === 'TREASURY_32NDS') {
    const points = Math.floor(value);
    const thirtySeconds = (value - points) * 32;
    const full = Math.floor(thirtySeconds);
    const eighths = Math.round((thirtySeconds - full) * 8);
    return `${points}'${String(full).padStart(2, '0')}${eighths === 4 ? '+' : eighths}`;
  }
  const precision = spec ? Math.max(0, Math.min(6, String(spec.tickSize).split('.')[1]?.length ?? 0)) : 2;
  return new Intl.NumberFormat(localeOf(language), { minimumFractionDigits: precision, maximumFractionDigits: precision }).format(value);
}

const orderStatusLabels: Record<Language, Readonly<Record<OrderStatus, string>>> = {
  en: {
    IN_TRANSMISSION: 'Transmitting…', ACTIVE: 'Active', PARTIALLY_FILLED: 'Partial', CANCEL_PENDING: 'Processing…',
    FILLED: 'Filled', CANCELED: 'Canceled', EXPIRED: 'Expired', REJECTED: 'Rejected', CANCELED_END: 'Canceled'
  },
  fr: {
    IN_TRANSMISSION: 'Transmission…', ACTIVE: 'Actif', PARTIALLY_FILLED: 'Partiel', CANCEL_PENDING: 'Traitement…',
    FILLED: 'Exécuté', CANCELED: 'Annulé', EXPIRED: 'Expiré', REJECTED: 'Refusé', CANCELED_END: 'Annulé'
  }
};

const attemptStatusLabels: Record<Language, Readonly<Record<AttemptStatus, string>>> = {
  en: {
    READY: 'Ready', RUNNING: 'In progress', PAUSED: 'Paused', EVENT_LOCK: 'Market event',
    DISCONNECTED_GRACE: 'Reconnecting', TECH_PAUSE: 'Technical pause', COMPLETED: 'Completed', ABORTED: 'Abandoned',
    INCIDENT: 'Technical incident', INSOLVENT: 'Insolvent'
  },
  fr: {
    READY: 'Prête', RUNNING: 'En cours', PAUSED: 'En pause', EVENT_LOCK: 'Événement marché',
    DISCONNECTED_GRACE: 'Reconnexion', TECH_PAUSE: 'Pause technique', COMPLETED: 'Terminée', ABORTED: 'Abandonnée',
    INCIDENT: 'Incident technique', INSOLVENT: 'Insolvabilité'
  }
};

export const formatOrderStatus = (status: string, language: Language): string => orderStatusLabels[language][status as OrderStatus] ?? status;
export const formatAttemptStatus = (status: string, language: Language): string => attemptStatusLabels[language][status as AttemptStatus] ?? status;
